import {queries} from '@/shared/integrations/zero'
import {ListProtocolsInput} from '@cypro/engine/queries'
import {Protocol} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type ProtocolStartRow = Pick<Protocol, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteProtocols({
  sort = {field: 'name', dir: 'asc'},
  filters,
  limit = 25,
}: Omit<Partial<ListProtocolsInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.field, sort?.dir, filters?.diagnosis, filters?.substances, filters?.cancerTypes, filters?.search, limit, search]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<Protocol, ProtocolStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              createdAt: start.createdAt,
              name: start.name,
              direction: dir,
            }
          : undefined

      return {
        query: queries.protocols.listProtocols({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Protocol> = useCallback(
    ({id}) => ({
      query: queries.protocols.getProtocolById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Protocol): ProtocolStartRow => ({
      id: row.id,
      createdAt: row.createdAt ?? 0,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback(
    (row: Protocol) => ({
      label: row.name,
      value: row.id,
      description: [row.diagnosis?.name, row.cancerType?.name].filter(Boolean).join(' | ') || undefined,
    }),
    []
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
